<script setup lang="ts">
import { computed } from 'vue';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

export type PillTabOption = {
    value: string;
    label: string;
    hidden?: boolean;
};

const props = defineProps<{
    tabs: PillTabOption[];
    class?: string;
    headerClass?: string;
    listClass?: string;
}>();

const modelValue = defineModel<string>({ required: true });

const onTabChange = (value: string | number) => {
    const next = String(value);
    if (next === modelValue.value) return;
    modelValue.value = next;
};

const visibleTabs = computed(() => props.tabs.filter((tab) => !tab.hidden));

const gridColsClass = computed(() => {
    const count = visibleTabs.value.length;
    if (count <= 1) return 'grid grid-cols-1';
    if (count === 2) return 'grid grid-cols-2';
    if (count === 3) return 'grid grid-cols-3';
    return 'grid';
});
</script>

<template>
    <Tabs
        :model-value="modelValue"
        :class="props.class"
        @update:model-value="onTabChange"
    >
        <div
            :class="cn('flex items-center justify-between gap-8', headerClass)"
        >
            <TabsList
                :class="
                    cn(
                        'w-fit rounded-full bg-background border border-primary30',
                        gridColsClass,
                        listClass
                    )
                "
            >
                <TabsTrigger
                    v-for="tab in visibleTabs"
                    :key="tab.value"
                    :value="tab.value"
                    class="rounded-full font-normal data-[state=active]:bg-primary30"
                >
                    {{ tab.label }}
                </TabsTrigger>
            </TabsList>
            <slot name="actions" />
        </div>
        <slot />
    </Tabs>
</template>
